import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { rupee, API } from "../lib/api";
import { OrderTimeline } from "../components/OrderTimeline";
import { Invoice } from "../components/Invoice";
import { Download, FileText, Mail } from "lucide-react";
import { toast } from "sonner";

export default function OrderView() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);

  useEffect(() => {
    api.get(`/orders/${orderId}`).then((r) => setOrder(r.data));
    api.get(`/invoices/${orderId}`).then((r) => setInvoice(r.data)).catch(() => {});
  }, [orderId]);

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API}/invoices/${orderId}/pdf`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Invoice-${invoice?.invoice_number || orderId}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice PDF downloaded");
    } catch { toast.error("Could not download invoice"); }
  };

  if (!order) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-muted-foreground">Loading order...</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="order-view-page">
      <div className="flex justify-between items-center mb-6">
        <div><h1 className="font-serif text-3xl text-navy">Order {order.order_number}</h1><div className="text-sm text-muted-foreground">{new Date(order.created_at).toLocaleString("en-IN")}</div></div>
        <span className="text-xs bg-gold/15 text-navy font-medium px-3 py-1.5 rounded-full capitalize">{order.status.replace(/_/g, " ")}</span>
      </div>

      <div className="bg-white rounded-xl border border-black/5 p-6 mb-5"><OrderTimeline status={order.status} /></div>

      <div className="bg-white rounded-xl border border-black/5 p-6 mb-5">
        <div className="space-y-2">
          {order.items.map((it, i) => (
            <div key={i} className="flex justify-between text-sm border-b border-dashed border-black/10 pb-2">
              <div><span className="text-navy font-medium">{it.name}</span> × {it.quantity}{it.variant && <span className="text-muted-foreground"> · {it.variant}</span>}
                {(it.customizations?.length > 0 || it.add_ons?.length > 0) && <div className="text-xs text-muted-foreground">{[...it.customizations, ...it.add_ons].map((o) => o.name).join(", ")}</div>}
              </div>
              <span className="text-muted-foreground">{rupee(it.total)}</span>
            </div>
          ))}
        </div>
        <div className="flex justify-between font-semibold text-lg text-navy mt-4"><span>Total</span><span>{rupee(order.grand_total)}</span></div>
        <div className="text-xs text-muted-foreground mt-1">Payment: <span className="capitalize">{order.payment_method}</span> · {order.payment_status}</div>
      </div>

      {invoice ? (
        <div className="flex flex-wrap gap-3 mb-5">
          <button onClick={() => setShowInvoice(!showInvoice)} data-testid="view-invoice-btn" className="flex items-center gap-2 bg-navy text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-navy-soft transition-colors"><FileText size={16} /> {showInvoice ? "Hide" : "View"} Invoice</button>
          <button onClick={downloadPdf} data-testid="download-invoice-btn" className="flex items-center gap-2 border border-navy/25 text-navy font-semibold px-5 py-2.5 rounded-full hover:border-gold transition-colors"><Download size={16} /> Download PDF</button>
          <button onClick={() => toast.success("Invoice emailed (architecture ready)")} data-testid="email-invoice-btn" className="flex items-center gap-2 border border-navy/25 text-navy font-semibold px-5 py-2.5 rounded-full hover:border-gold transition-colors"><Mail size={16} /> Email Invoice</button>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground mb-5">Invoice will be available once payment is confirmed.</div>
      )}

      {showInvoice && invoice && <div className="border border-black/10 rounded-xl overflow-hidden print:border-0"><Invoice invoice={invoice} /></div>}

      <Link to="/account" className="inline-flex mt-4 text-sm font-semibold text-navy hover:text-gold">← Back to My Orders</Link>
    </div>
  );
}
